import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar, setUnsavedLotChanges } from "../../redux/actions/index";

// Material UI
import {
  Card, Alert, CircularProgress, Box, Fade, CssBaseline, Typography
} from "@mui/material";

// Layout & UI Components
import NavTab from "../../layout/NavTab";
import AddLot from "../../components/Lots/AddLot";
import ProjectDetails from "../../components/Projects/ProjectDetails";
import LotDetails from "../Lots/LotDetails";
import AlerteDialog from "../../components/AlerteDialog/AlerteDialog";
import { useRef } from "react";

// Fetch & Types
import { getOne, fetchGetProtect } from "../../fetchs/get";
import { fetchPutProtect } from "../../fetchs/put";
import { fetchPostProtect } from "../../fetchs/post";
import { IProject } from "../../interface/project";

interface IFormValues {
  name: string;
  description: string;
  client: string;
}

const Project = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const [project, setProject] = useState<IProject>();
  const [error, setError] = useState("");
  const [isModified, setIsModified] = useState<boolean>(false);
  const [isModifiedMatrice, setIsModifiedMatrice] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [lotId, setLotIdRaw] = useState<string>("");
  const [showDialog, setShowDialog] = useState(false);
  const [pendingLotId, setPendingLotId] = useState<string | null>(null);

  // Signale globalement (redux) si le lot affiché a des modifs non enregistrées,
  // pour que des composants hors de cette page (ex. la sidebar) puissent aussi
  // demander confirmation avant de faire quitter la page.
  useEffect(() => {
    dispatch(setUnsavedLotChanges(!!(lotId && showDialog)));
  }, [lotId, showDialog, dispatch]);

  const applyLotIdChange = (id: string) => {
    if (id && !lotId) {
      // Entrée dans un lot → pousser un état dans l'historique
      window.history.pushState({ lotId: id }, "");
    }
    setLotIdRaw(id);
  };

  // Wrapper setLotId : demande confirmation si le lot en cours a des modifs
  // non enregistrées avant de changer de lot ou de revenir au projet.
  const setLotId = (id: string) => {
    if (lotId && showDialog && id !== lotId) {
      setPendingLotId(id);
      return;
    }
    applyLotIdChange(id);
  };

  const confirmPendingLotChange = () => {
    if (pendingLotId !== null) {
      applyLotIdChange(pendingLotId);
      setShowDialog(false);
      setPendingLotId(null);
    }
  };

  const cancelPendingLotChange = () => setPendingLotId(null);

  // Intercepter le bouton retour du navigateur
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (lotId) {
        // On est dans un lot → retour au projet (pas de navigation URL)
        setLotIdRaw("");
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [lotId]);

  const token = useSelector((state: RootState) => state.user.token);
  const author = useSelector((state: RootState) => state.user.username);

  const getProject = () => {
    if (token && params.projectId) {
      getOne(
        token,
        "/projects/",
        params.projectId,
        setProject,
        () => dispatch(sessionEnd()),
        (err: any) => {
          navigate(`/projets/`);
          dispatch(showSnackbar("Erreur : " + err?.error, "error"));
        }
      );
    }
  };

  useEffect(() => {
    getProject();
    if (state === "projectSuccess") {
      dispatch(showSnackbar("Projet créé avec succès", "success"));
    }
  }, []);

  const hasAutoRedirected = useRef(false);

  const isTmaProject = project ? (project.isTma !== undefined ? project.isTma : !!(project.name && project.name.toLowerCase().includes("tma"))) : false;

  useEffect(() => {
    if (isTmaProject && !lotId && !hasAutoRedirected.current) {
      hasAutoRedirected.current = true;
      if (project!.lots && project!.lots.length > 0) {
        setLotId(project!.lots[0]._id);
      } else {
        createDefaultTmaLot();
      }
    }
  }, [project, lotId]);

  const createDefaultTmaLot = async () => {
    if (!token || !project) return;
    
    let matrixData: any[] = [];
    let basedOn = project.defaultMatrice || "";
    
    if (project.defaultMatrice) {
      try {
        const matrixId = typeof project.defaultMatrice === 'object' ? (project.defaultMatrice as any)._id : project.defaultMatrice;
        const matrixRes = await fetchGetProtect(token, "/matrice/id/", matrixId);
        if (matrixRes.ok) {
          const matrixJson = await matrixRes.json();
          matrixData = matrixJson.message?.data || [];
        }
      } catch (e) {
        console.error("Failed to fetch default matrix", e);
      }
    }

    // Si la matrice n'est toujours pas remplie, on récupère le master template
    if (matrixData.length === 0) {
      try {
        const matrixRes = await fetchGetProtect(token, "/matrice/master/", "project_master");
        if (matrixRes.ok) {
          const matrixJson = await matrixRes.json();
          matrixData = matrixJson.message?.data || [];
          basedOn = matrixJson.message?._id || "";
        }
      } catch (e) {
        console.error("Failed to fetch master matrix fallback", e);
      }
    }

    const body = JSON.stringify({
      projectId: project._id,
      name: "Tâches TMA",
      basedOn,
      matrice: matrixData, 
      author: author || "Système",
    });
    try {
      const response = await fetchPostProtect(token, "/lots/add", body);
      if (response.status === 200 || response.status === 201) {
        const data = await response.json();
        // Le lot a été créé, autoriser la redirection au prochain render
        hasAutoRedirected.current = false;
        getProject();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateProject = async (values: IFormValues) => {
    const body = JSON.stringify({
      id: project?._id,
      name: values.name,
      description: values.description,
      client: values.client,
    });
    if (token) {
      fetchPutProtect(token, "/projects/update", body)
        .then((response) => {
          if (response.status === 200) {
            setError("");
            dispatch(showSnackbar("Métadonnées modifiées", "success"));
            response.json().then(() => {
              setIsModified(false);
              setIsModifiedMatrice(false);
              getProject();
            });
          } else if (response.status === 401) {
            dispatch(sessionEnd());
          } else {
            response.json().then((err) => {
              setError("Erreur : " + err.error);
              dispatch(showSnackbar("Erreur lors de la modification", "error"));
            });
          }
        })
        .catch((err) => console.error(err));
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      
      <NavTab
        text={lotId ? (isTmaProject ? "Gestion des tâches TMA" : "Détails du lot") : `Projet : ${project?.name || ""}`}
        project={project}
        setOpenModal={setOpenModal}
        setLotId={setLotId}
        lotId={lotId}
        showDialog={showDialog}
        displayReturn={!isTmaProject}
      />

      <Card
        elevation={0}
        sx={{
          mx: 3,
          mb: 3,
          p: 3,
          borderRadius: "24px", // Cohérence avec les autres pages
          border: "1px solid #e2e8f0",
          bgcolor: isModified ? "rgba(255,255,255,0.6)" : "white",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
          overflow: "hidden" // Empêche le débordement des enfants
        }}
      >
        {project?.client ? (
          <Fade in={true} timeout={500}>
            <Box 
              className="scrollbar" 
              sx={{ 
                overflowY: "auto", 
                height: "100%",
                pr: 1 // Espace pour la scrollbar
              }}
            >
              {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

              {!lotId ? (
                <ProjectDetails
                  project={project}
                  isModified={isModified}
                  setOpenModal={setOpenModal}
                  setIsModified={setIsModified}
                  isModifiedMatrice={isModifiedMatrice}
                  setIsModifiedMatrice={setIsModifiedMatrice}
                  updateProject={updateProject}
                  setLotId={setLotId}
                  lotId={lotId}
                />
              ) : (
                <LotDetails
                  project={project}
                  lotId={lotId}
                  setLotId={setLotId}
                  showDialog={showDialog}
                  setShowDialog={setShowDialog}
                  refreshProject={getProject}
                  setOpenModal={setOpenModal}
                />
              )}

              <AddLot
                project={project}
                setProject={setProject}
                openModal={openModal}
                setOpenModal={setOpenModal}
                setLotId={setLotId}
                lotId={lotId}
              />
            </Box>
          </Fade>
        ) : (
          <Box sx={{ 
            flexGrow: 1, 
            display: "flex", 
            flexDirection: "column", 
            justifyContent: "center", 
            alignItems: "center",
            gap: 2 
          }}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Chargement du projet...
            </Typography>
          </Box>
        )}
      </Card>

      <AlerteDialog
        open={pendingLotId !== null}
        handleClose={cancelPendingLotChange}
        deleteProject={confirmPendingLotChange}
        title="Modifications non enregistrées"
        contentText="Voulez-vous quitter sans enregistrer les modifications du lot ?"
      />
    </Box>
  );
};

export default Project;